import React, { useEffect, useState } from "react";
import { Loader2, Play, BellRing } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Separator } from "../ui/separator";

const RULE_DEFS = [
  { key: "quote_not_viewed", label: "Devis envoyé non consulté", field: "days", unit: "jours après envoi" },
  { key: "quote_viewed_not_accepted", label: "Devis consulté non accepté", field: "days", unit: "jours après consultation" },
  { key: "quote_expiring", label: "Devis bientôt expiré", field: "daysBefore", unit: "jours avant expiration" },
  { key: "invoice_due_soon", label: "Facture bientôt échue", field: "daysBefore", unit: "jours avant échéance" },
];

const KIND_LABELS = {
  quote_not_viewed: "Devis non consulté",
  quote_viewed_not_accepted: "Devis consulté non accepté",
  quote_expiring: "Devis bientôt expiré",
  invoice_due_soon: "Facture bientôt échue",
  invoice_overdue: "Facture impayée",
};

const ReminderSettingsDialog = ({ open, onOpenChange }) => {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [overdueDays, setOverdueDays] = useState("3,7");

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        API.get("/reminders/settings"),
        API.get("/reminders", { params: { limit: 50 } }),
      ]);
      setSettings(s.data);
      setOverdueDays((s.data.rules?.invoice_overdue?.days || [3, 7]).join(","));
      setLogs(l.data.reminders || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setRule = (key, patch) =>
    setSettings((s) => ({ ...s, rules: { ...s.rules, [key]: { ...s.rules[key], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      const rules = { ...settings.rules };
      rules.invoice_overdue = {
        ...rules.invoice_overdue,
        days: overdueDays.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n)),
      };
      await API.put("/reminders/settings", { enabled: settings.enabled, appBaseUrl: settings.appBaseUrl, rules });
      toast.success("Réglages des relances enregistrés");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await API.post("/reminders/run");
      toast.success(`Relances exécutées : ${data.summary.quotes} devis, ${data.summary.invoices} factures`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reminder-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BellRing className="w-5 h-5" /> Relances automatiques</DialogTitle>
          <DialogDescription>
            Le planificateur vérifie chaque jour les devis et factures à relancer. Aucune relance le week-end ? non : tous les jours, une seule par document et par jour.
          </DialogDescription>
        </DialogHeader>

        {loading || !settings ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(207,78%,42%)]" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
              <div>
                <p className="font-medium text-sm">Relances automatiques activées</p>
                <p className="text-xs text-muted-foreground">Interrupteur général</p>
              </div>
              <Switch checked={settings.enabled} onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))} data-testid="reminders-global-switch" />
            </div>

            <div className="space-y-3">
              {RULE_DEFS.map((r) => (
                <div key={r.key} className="flex items-center gap-3 flex-wrap border rounded-lg p-3" data-testid={`rule-${r.key}`}>
                  <Switch checked={settings.rules[r.key]?.enabled} onCheckedChange={(v) => setRule(r.key, { enabled: v })} data-testid={`rule-switch-${r.key}`} />
                  <span className="text-sm font-medium flex-1">{r.label}</span>
                  <Input
                    type="number" min="0"
                    className="w-20"
                    value={settings.rules[r.key]?.[r.field] ?? 0}
                    onChange={(e) => setRule(r.key, { [r.field]: parseInt(e.target.value, 10) || 0 })}
                    data-testid={`rule-days-${r.key}`}
                  />
                  <span className="text-xs text-muted-foreground">{r.unit}</span>
                </div>
              ))}

              <div className="flex items-center gap-3 flex-wrap border rounded-lg p-3" data-testid="rule-invoice_overdue">
                <Switch checked={settings.rules.invoice_overdue?.enabled} onCheckedChange={(v) => setRule("invoice_overdue", { enabled: v })} data-testid="rule-switch-invoice_overdue" />
                <span className="text-sm font-medium flex-1">Facture impayée (paliers)</span>
                <Input className="w-28" value={overdueDays} onChange={(e) => setOverdueDays(e.target.value)} data-testid="rule-days-invoice_overdue" />
                <span className="text-xs text-muted-foreground">jours après échéance (ex : 3,7)</span>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={runNow} disabled={running} data-testid="reminders-run-now">
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Exécuter les relances maintenant
              </Button>
              <Button className="btn-primary" onClick={save} disabled={saving} data-testid="reminders-save">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enregistrer
              </Button>
            </div>

            <Separator />
            <div>
              <p className="font-semibold text-sm text-slate-800 mb-2">Historique des relances</p>
              {logs.length === 0 ? (
                <p className="text-xs text-muted-foreground">Aucune relance envoyée pour le moment.</p>
              ) : (
                <div className="space-y-1 max-h-52 overflow-y-auto" data-testid="reminders-log">
                  {logs.map((r, i) => (
                    <div key={i} className="text-xs flex items-center gap-2 border-b py-1">
                      <span className="text-muted-foreground">{(r.at || "").slice(0, 16).replace("T", " ")}</span>
                      <span className="font-mono text-[hsl(207,78%,42%)]">{r.docNumber || "—"}</span>
                      <span>{KIND_LABELS[r.kind] || r.kind}</span>
                      <span className="text-muted-foreground">→ {r.to}</span>
                      {r.status === "failed" && <span className="text-red-500">(échec)</span>}
                      {r.auto === false && <span className="text-[10px] bg-slate-200 px-1 rounded">manuel</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReminderSettingsDialog;
